import { T } from 'src/components/widgets/helpers/helpers';
import { PropertiesPanelControl, PropertiesPanelLabel, PropertiesPanelProperty } from './propertiesPanelProperty';
import React, { ButtonHTMLAttributes, useEffect, useState } from 'react';
import styled from 'styled-components';
import { BasicButton } from 'src/components/widgets/buttons';
import { ColorPicker, ColorPickerProps } from 'src/components/widgets/colorPicker';
import { useDropdown } from 'src/components/widgets/hooks/useDropdown';
import { FormControl } from 'src/components/widgets/formControl';
import { isNearWhiteOrBlack } from 'src/components/widgets/helpers/helpers';
import { useIsSceneLook } from './propertiesPanelLook';
import { panel } from './panelTheme';

type ColorPickerButtonProps = {
    $color: string | null;
    $block?: boolean;
    $computedColor?: number;
} & React.DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;
const ColorPickerButton = styled(BasicButton)<ColorPickerButtonProps>`
    padding: 4px 16px;
    font-size: 12px;
    min-height: unset;
    min-width: unset;
    background-color: ${(props) => props.$color};

    &:after {
        content: '${(props) => props.$color}';
        color: ${(props) => (props.$computedColor ? '#000' : '#fff')};
        mix-blend-mode: ${(props) => (props.$computedColor === -1 ? 'soft-light' : 'hard-light')};
    }

    &:hover:not([disabled]) {
        opacity: 0.8;
        background-color: ${(props) => props.$color} !important;
        color: ${(props) => props.$color} !important;
        &:after {
            color: ${(props) => (props.$computedColor ? '#000' : '#fff')} !important;
        }
    }

    ${(props) =>
        props.$block &&
        `
		border-radius: 4px;
		width: 100%;
		border: 1px #ddd solid;
	`}
`;

export type PropertiesPanelColorPickerButtonProps = {
    color: string;
    colorPickerProps?: Omit<ColorPickerProps, 'color' | 'onChange'>;
    onChange: ColorPickerProps['onChange'];
    disabled?: boolean;
    block?: boolean;
};

export const PropertiesPanelColorPickerButton: React.FC<PropertiesPanelColorPickerButtonProps> = ({ color, colorPickerProps, onChange, disabled, block }) => {
    const [setOpen, , isOpen, Dropdown] = useDropdown();
    return (
        <FormControl>
            <ColorPickerButton
                $block={block}
                $color={color}
                $computedColor={isNearWhiteOrBlack(color ?? '#ffffff')}
                $size='medium'
                onClick={(e) => setOpen(e.currentTarget, 'bottom', 'left')}
                disabled={disabled}
            />
            {isOpen && (
                <Dropdown>
                    <ColorPicker
                        {...colorPickerProps}
                        color={color}
                        onChange={(newColor) => {
                            onChange(newColor);
                        }}
                    />
                </Dropdown>
            )}
        </FormControl>
    );
};

// IL COLORE È UN CAMPO SOLO (round 4, pacchetto D; utente, 2026-10-14): dentro, il campione che apre
// il selettore, l'esadecimale che si scrive e il contagocce che il colore lo va a prendere dal palco.
// Sono tre strade per la stessa cosa, e un campo solo lo dice meglio di tre pezzi accostati. Il
// contagocce resta sempre visibile, a differenza del prototipo: un comando che compare solo al
// passaggio non lo trova chi non sa già che c'è.
//
// Il campione e il codice stanno separati, e non il codice scritto sul campione: su un colore chiaro
// il testo bianco sparirebbe. Così il colore lo si vede e il numero lo si legge, sempre.
const ColorField = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    height: 26px;
    border: 1px solid ${panel.line};
    border-radius: 5px;
    background: ${panel.field};
    overflow: hidden;

    &:focus-within {
        border-color: ${panel.accent};
    }
`;

const Chip = styled.button<{ $color: string }>`
    flex: none;
    width: 16px;
    height: 16px;
    margin: 0 4px;
    padding: 0;
    border: 0;
    border-radius: 3px;
    background: ${(props) => props.$color};
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.14);
    cursor: pointer;

    &:hover:not([disabled]) {
        box-shadow: inset 0 0 0 1px ${panel.inkMuted};
    }

    &[disabled] {
        cursor: not-allowed;
        opacity: 0.55;
    }
`;

const Pick = styled.button`
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${panel.inkMuted};
    cursor: pointer;

    svg {
        width: 14px;
        height: 14px;
    }

    &:hover:not([disabled]) {
        color: ${panel.ink};
    }

    &[disabled] {
        cursor: not-allowed;
        opacity: 0.55;
    }
`;

const Hex = styled.input`
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: ${panel.ink};

    &:focus {
        outline: none;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

const PipetteGlyph = () => (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round'>
        <path d='M16.8 3.4a2.9 2.9 0 0 1 4.1 4.1l-2 2-4.1-4.1Z' />
        <path d='m13.4 6.8 4.1 4.1-8.2 8.2-4.8 1.2 1.2-4.8Z' />
        <path d='m11 9.2 4.1 4.1' />
    </svg>
);

const HEX = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// IL COLORE DA SOLO, senza la riga intorno — come `PropertiesPanelSwitch`: serve dove la riga la
// disegna qualcun altro, per esempio le Settings, che hanno righe loro.
export const PropertiesPanelColorControl: React.FC<{ id: string; value: string; onChange?: (value: string) => void; disabled?: boolean }> = ({
    id,
    value,
    onChange,
    disabled
}) => {
    const [openDropdown, , isOpen, Dropdown] = useDropdown();

    // IL CAMPO SI SCRIVE, QUINDI HA UNO STATO SUO. Rimandando ogni battuta al colore vero,
    // «#EA» diventerebbe un colore non valido e il campo si riscriverebbe sotto le dita: si
    // consegna quando quello che c'è scritto è un colore.
    const [typed, setTyped] = useState(value);

    useEffect(() => setTyped(value), [value]);

    const commit = (text: string) => {
        if (!HEX.test(text.trim())) {
            setTyped(value);
            return;
        }

        const clean = text.trim();
        onChange?.(clean.startsWith('#') ? clean : `#${clean}`);
    };

    // IL CONTAGOCCE È DEL BROWSER, e non tutti ce l'hanno: dove non c'è, il pulsante non c'è —
    // meglio due strade che tre, di cui una che non fa niente.
    const eyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window ? (window as any).EyeDropper : undefined;

    return (
        <>
            <ColorField>
                <Chip
                    id={id}
                    type='button'
                    $color={value}
                    disabled={disabled}
                    title={T._('Pick a color', 'Admin')}
                    aria-label={T._('Pick a color', 'Admin')}
                    onClick={(e) => openDropdown(e.currentTarget, 'bottom-start')}
                />
                <Hex
                    value={typed}
                    spellCheck={false}
                    disabled={disabled}
                    aria-label={T._('Color value', 'Admin')}
                    onChange={(e) => setTyped(e.target.value)}
                    onBlur={(e) => commit(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
                        if (e.key === 'Escape') setTyped(value);
                    }}
                />
                {eyeDropper && (
                    <Pick
                        type='button'
                        disabled={disabled}
                        title={T._('Sample a color', 'Admin')}
                        aria-label={T._('Sample a color', 'Admin')}
                        onClick={async () => {
                            try {
                                const result = await new eyeDropper().open();
                                if (result?.sRGBHex) onChange?.(result.sRGBHex);
                            } catch {
                                // Chi annulla il campionamento non ha sbagliato niente.
                            }
                        }}
                    >
                        <PipetteGlyph />
                    </Pick>
                )}
            </ColorField>
            {isOpen && (
                <Dropdown>
                    <ColorPicker color={value} onChange={(newColor) => onChange?.(newColor)} />
                </Dropdown>
            )}
        </>
    );
};

export type PropertiesPanelColorPropertyProps = {
    id: string;
    label: React.ReactNode;
    value: string;
    onChange?: (newValue: string) => void;
    disabled?: boolean;
    block?: boolean;
};
export const PropertiesPanelColorProperty: React.FC<PropertiesPanelColorPropertyProps> = ({ id, label, value, onChange, disabled, block }) => {
    const scene = useIsSceneLook();

    return (
        <PropertiesPanelProperty disabled={disabled}>
            <PropertiesPanelLabel htmlFor={id}>{label}</PropertiesPanelLabel>
            {scene ? (
                <PropertiesPanelColorControl id={id} value={value} onChange={onChange} disabled={disabled} />
            ) : (
                <PropertiesPanelControl>
                    <PropertiesPanelColorPickerButton color={value} onChange={(color) => onChange?.(color)} disabled={disabled} block={block} />
                </PropertiesPanelControl>
            )}
        </PropertiesPanelProperty>
    );
};
